import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  alpha,
  Tooltip,
  CircularProgress,
  Alert,
  AlertTitle,
  Collapse,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState, useMemo, useEffect } from 'react';

import { useCompleteStagedFileUpload } from '@/api/endpoints/vault/useCompleteStagedFileUpload';
import { StagedFile } from '@/api/endpoints/vault/useGetStagedFiles';
import { useStreamStagedFolderUpload } from '@/api/endpoints/vault/useStreamStagedFolderUpload';
import { ChevronDownIcon } from '@/assets/icons/ChevronDownIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { ClockIcon } from '@/assets/icons/ClockIcon';
import { DocumentIcon } from '@/assets/icons/DocumentIcon';
import { EyeIcon } from '@/assets/icons/EyeIcon';
import { File07Icon } from '@/assets/icons/File07Icon';
import { FolderIcon } from '@/assets/icons/FolderIcon';
import { Image01Icon } from '@/assets/icons/Image01Icon';
import { MarkerPin01Icon } from '@/assets/icons/MarkerPin01Icon';
import { getFromNow } from '@/utils/date';

// Local utility function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

interface FolderGroup {
  folderPath: string;
  files: StagedFile[];
  totalSize: number;
}

interface StagedFilesHierarchicalViewProps {
  stagedFiles: StagedFile[];
  availableLocations: Array<{ id: number; name: string; address_text?: string }>;
  isLoading?: boolean;
  onPreviewFile?: (fileId: string) => void;
  onFileAssigned?: () => void;
}

const StagedFilesHierarchicalView: React.FC<StagedFilesHierarchicalViewProps> = ({
  stagedFiles,
  availableLocations,
  isLoading = false,
  onPreviewFile,
  onFileAssigned,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  // State for individual file assignment
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

  // State for folder assignment
  const [folderAnchorEl, setFolderAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFolderPath, setSelectedFolderPath] = useState<string | null>(null);

  // State for folder expansion
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  // State to track recently assigned folders (hide them immediately)
  const [recentlyAssignedFolders, setRecentlyAssignedFolders] = useState<Set<string>>(new Set());
  const [recentlyAssignedFiles, setRecentlyAssignedFiles] = useState<Set<string>>(new Set());

  const completeStagedUploadMutation = useCompleteStagedFileUpload();

  // Use streaming hook for folder uploads with real-time progress
  const { startStream: streamFolderUpload, streamState } = useStreamStagedFolderUpload();

  // Clear recently assigned state when staged files actually change
  useEffect(() => {
    // Clear assigned folders that no longer have any files in stagedFiles
    setRecentlyAssignedFolders(prev => {
      const currentFolderPaths = new Set(
        stagedFiles.map(file => file.folder_structure?.folder_path || 'Root')
      );
      const stillRelevant = new Set(
        [...prev].filter(folderPath => currentFolderPaths.has(folderPath))
      );
      return stillRelevant;
    });

    // Clear assigned files that are no longer in stagedFiles
    setRecentlyAssignedFiles(prev => {
      const currentFileIds = new Set(stagedFiles.map(file => file.id));
      const stillRelevant = new Set([...prev].filter(fileId => currentFileIds.has(fileId)));
      return stillRelevant;
    });
  }, [stagedFiles]);

  // Group files by folder, filtering out recently assigned items
  const folderGroups = useMemo(() => {
    const groups: Record<string, FolderGroup> = {};

    // Filter out recently assigned files
    const visibleFiles = stagedFiles.filter(file => !recentlyAssignedFiles.has(file.id));

    visibleFiles.forEach(file => {
      const folderPath = file.folder_structure?.folder_path || 'Root';

      // Skip folders that were recently assigned
      if (recentlyAssignedFolders.has(folderPath)) {
        return;
      }

      if (!groups[folderPath]) {
        groups[folderPath] = {
          folderPath,
          files: [],
          totalSize: 0,
        };
      }

      groups[folderPath].files.push(file);
      groups[folderPath].totalSize += file.file_size;
    });

    return Object.values(groups).sort((a, b) => {
      // Put 'Root' folder first, then alphabetical
      if (a.folderPath === 'Root') return -1;
      if (b.folderPath === 'Root') return 1;
      return a.folderPath.localeCompare(b.folderPath);
    });
  }, [stagedFiles, recentlyAssignedFiles, recentlyAssignedFolders]);

  // File assignment handlers
  const handleFileMenuOpen = (event: React.MouseEvent<HTMLElement>, fileId: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedFileId(fileId);
  };

  const handleFileMenuClose = () => {
    setAnchorEl(null);
    setSelectedFileId(null);
  };

  const handleAssignFileToLocation = async (locationId: number) => {
    if (!selectedFileId) return;

    // Close the dropdown immediately when location is selected
    handleFileMenuClose();

    try {
      // Immediately hide the file
      setRecentlyAssignedFiles(prev => new Set([...prev, selectedFileId]));

      await completeStagedUploadMutation.mutateAsync({
        stagedDocumentId: selectedFileId,
        locationId,
      });

      await queryClient.invalidateQueries({
        queryKey: ['GET', 'document/google-drive/staged-files'],
      });

      if (onFileAssigned) {
        onFileAssigned();
      }
    } catch (error) {
      // If assignment failed, show the file again
      setRecentlyAssignedFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedFileId);
        return newSet;
      });
    }
  };

  // Folder assignment handlers
  const handleFolderMenuOpen = (event: React.MouseEvent<HTMLElement>, folderPath: string) => {
    event.stopPropagation();
    setFolderAnchorEl(event.currentTarget);
    setSelectedFolderPath(folderPath);
  };

  const handleFolderMenuClose = () => {
    setFolderAnchorEl(null);
    setSelectedFolderPath(null);
  };

  const handleAssignFolderToLocation = async (locationId: number) => {
    if (!selectedFolderPath || selectedFolderPath === 'Root') return;

    // Close the dropdown immediately when location is selected
    handleFolderMenuClose();

    try {
      // Immediately hide the folder
      setRecentlyAssignedFolders(prev => new Set([...prev, selectedFolderPath]));

      // Use streaming upload for real-time progress and vault refresh
      await streamFolderUpload({
        folderPath: selectedFolderPath,
        locationId,
      });

      // The streaming hook will handle query invalidation automatically
      // when it receives 'refresh_vault' actions from the backend

      if (onFileAssigned) {
        onFileAssigned();
      }
    } catch (error) {
      // If assignment failed, show the folder again
      setRecentlyAssignedFolders(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedFolderPath);
        return newSet;
      });
    }
  };

  // Folder expansion handlers
  const handleToggleFolder = (folderPath: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <Image01Icon />;
    }
    if (mimeType.includes('pdf')) {
      return <File07Icon />;
    }
    return <DocumentIcon />;
  };

  const handleFileClick = (file: StagedFile) => {
    if (onPreviewFile) {
      onPreviewFile(file.id);
    } else if (file.metadata.google_drive_web_view_link) {
      window.open(file.metadata.google_drive_web_view_link, '_blank', 'noopener,noreferrer');
    }
  };

  const renderFileCard = (file: StagedFile) => {
    const isCurrentlyAssigning =
      completeStagedUploadMutation.isPending && selectedFileId === file.id;

    return (
      <Card
        key={file.id}
        variant="outlined"
        sx={{
          ml: 1,
          cursor: isCurrentlyAssigning ? 'default' : 'pointer',
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          opacity: isCurrentlyAssigning ? 0.8 : 1,
          borderColor: isCurrentlyAssigning ? theme.palette.primary.main : undefined,
          '&:hover': isCurrentlyAssigning
            ? {}
            : {
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderColor: theme.palette.primary.main,
              transform: 'translateY(-1px)',
            },
        }}
        onClick={() => !isCurrentlyAssigning && handleFileClick(file)}
      >
        {/* Loading overlay */}
        {isCurrentlyAssigning && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 1,
              zIndex: 1,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="primary.main" fontWeight={500}>
                Assigning to location...
              </Typography>
            </Stack>
          </Box>
        )}

        <CardContent sx={{ py: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            >
              {getFileIcon(file.mime_type)}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" noWrap sx={{ mb: 0.5 }}>
                  {file.original_name}
                </Typography>
                {isCurrentlyAssigning && <CircularProgress size={16} color="primary" />}
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(file.file_size)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  •
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getFromNow(new Date(file.created_at))}
                </Typography>
              </Stack>
            </Box>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title="Preview file">
                <span>
                  <IconButton
                    size="small"
                    disabled={isCurrentlyAssigning}
                    onClick={e => {
                      e.stopPropagation();
                      handleFileClick(file);
                    }}
                  >
                    <EyeIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Button
                variant="outlined"
                size="small"
                startIcon={
                  isCurrentlyAssigning ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <MarkerPin01Icon />
                  )
                }
                onClick={e => {
                  e.stopPropagation();
                  handleFileMenuOpen(e, file.id);
                }}
                disabled={completeStagedUploadMutation.isPending}
                sx={{ borderRadius: 1.5 }}
              >
                {isCurrentlyAssigning ? 'Assigning...' : 'Assign'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderFolderGroup = (group: FolderGroup) => {
    const isExpanded = expandedFolders.has(group.folderPath);
    const isRoot = group.folderPath === 'Root';
    const isCurrentlyAssigningFolder =
      streamState.isStreaming && selectedFolderPath === group.folderPath;

    return (
      <Box key={group.folderPath} sx={{ mb: 2 }}>
        <Card
          variant="outlined"
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            borderColor: alpha(theme.palette.primary.main, 0.2),
          }}
        >
          <CardContent sx={{ py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton
                size="small"
                onClick={() => handleToggleFolder(group.folderPath)}
                sx={{ color: theme.palette.primary.main }}
              >
                {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
              </IconButton>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              >
                <FolderIcon />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  {group.folderPath}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {group.files.length} file{group.files.length !== 1 ? 's' : ''} •{' '}
                  {formatFileSize(group.totalSize)}
                </Typography>
              </Box>

              {!isRoot && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    isCurrentlyAssigningFolder ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <MarkerPin01Icon />
                    )
                  }
                  onClick={e => {
                    e.stopPropagation();
                    handleFolderMenuOpen(e, group.folderPath);
                  }}
                  disabled={streamState.isStreaming}
                  sx={{ borderRadius: 1.5 }}
                >
                  {isCurrentlyAssigningFolder ? 'Processing...' : 'Assign Folder'}
                </Button>
              )}
            </Stack>

            {/* Show streaming progress for this folder */}
            {isCurrentlyAssigningFolder && streamState.progress > 0 && (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {streamState.currentFile || 'Processing...'}
                  </Typography>
                  <Typography variant="body2" color="primary.main">
                    {Math.round(streamState.progress)}%
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    width: '100%',
                    height: 4,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${streamState.progress}%`,
                      height: '100%',
                      bgcolor: theme.palette.primary.main,
                      transition: 'width 0.3s ease-in-out',
                    }}
                  />
                </Box>
                {streamState.processedFiles > 0 && streamState.totalFiles && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {streamState.processedFiles} of {streamState.totalFiles} files processed
                  </Typography>
                )}
              </Box>
            )}

            <Collapse in={isExpanded}>
              <Box sx={{ mt: 2 }}>
                <Stack spacing={1}>{group.files.map(file => renderFileCard(file))}</Stack>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Show loading state if initial load or if any folder assignment is in progress
  if (isLoading) {
    return (
      <Card sx={{ bgcolor: 'common.white', mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading pending files...
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // Show assignment progress state when a folder is being assigned
  if (streamState.isStreaming) {
    return (
      <Card
        variant="outlined"
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          borderColor: alpha(theme.palette.primary.main, 0.2),
          p: 3,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={40} />
          <Typography variant="h6" textAlign="center">
            Processing Folder Assignment
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {streamState.currentFile || 'Preparing files for processing...'}
          </Typography>
          {streamState.progress > 0 && (
            <Typography variant="body2" color="primary.main">
              {Math.round(streamState.progress)}% Complete
            </Typography>
          )}
          {streamState.errors.length > 0 && (
            <Alert severity="warning" sx={{ width: '100%' }}>
              <AlertTitle>Some files had errors</AlertTitle>
              {streamState.errors.slice(0, 3).map(error => (
                <Typography key={error} variant="body2">
                  {error}
                </Typography>
              ))}
              {streamState.errors.length > 3 && (
                <Typography variant="body2">
                  +{streamState.errors.length - 3} more errors
                </Typography>
              )}
            </Alert>
          )}
        </Stack>
      </Card>
    );
  }

  // Check if all files/folders have been assigned (including recently assigned ones)
  const allItemsAssigned = folderGroups.length === 0 && stagedFiles.length > 0;

  if (!stagedFiles || stagedFiles.length === 0 || allItemsAssigned) {
    return null;
  }

  return (
    <Card
      sx={{
        bgcolor: 'common.white',
        mb: 3,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        borderLeft: `4px solid ${theme.palette.primary.main}`,
      }}
    >
      <CardContent>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClockIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" color="primary.main">
              Pending Location Assignment
            </Typography>
            <Chip
              label={`${stagedFiles.length} file${stagedFiles.length === 1 ? '' : 's'}`}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
              variant="outlined"
            />
            <Chip
              label={`${folderGroups.length} folder${folderGroups.length === 1 ? '' : 's'}`}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.info.main, 0.1),
                color: theme.palette.info.main,
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              }}
              variant="outlined"
            />
          </Box>

          <Alert severity="info">
            <AlertTitle>Assign Files by Folder or Individually</AlertTitle>
            Click "Assign Folder" to assign all files in a folder at once, or expand folders to
            assign individual files. Files will be auto-organized into the appropriate folder in the
            assigned location.
            <br />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              💡 <strong>Tip:</strong> Assigning folders is faster for bulk operations!
            </Typography>
          </Alert>

          <Stack spacing={2}>{folderGroups.map(group => renderFolderGroup(group))}</Stack>
        </Stack>
      </CardContent>

      {/* Individual file assignment menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleFileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {availableLocations.map(location => (
          <MenuItem
            key={location.id}
            onClick={() => handleAssignFileToLocation(location.id)}
            disabled={completeStagedUploadMutation.isPending}
          >
            <Stack>
              <Typography variant="body2">{location.name}</Typography>
              {location.address_text && (
                <Typography variant="caption" color="text.secondary">
                  {location.address_text}
                </Typography>
              )}
            </Stack>
          </MenuItem>
        ))}
      </Menu>

      {/* Folder assignment menu */}
      <Menu
        anchorEl={folderAnchorEl}
        open={Boolean(folderAnchorEl)}
        onClose={handleFolderMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {availableLocations.map(location => (
          <MenuItem
            key={location.id}
            onClick={() => handleAssignFolderToLocation(location.id)}
            disabled={streamState.isStreaming}
          >
            <Stack>
              <Typography variant="body2" fontWeight={500}>
                {location.name}
              </Typography>
              {location.address_text && (
                <Typography variant="caption" color="text.secondary">
                  {location.address_text}
                </Typography>
              )}
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Card>
  );
};

export default StagedFilesHierarchicalView;
